import {
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	IS_NOT_EMPTY,
	IS_EMPTY,
	ProjectDTO,
	DATE_YMD,
	TOMORROW,
	Location,
} from 'cargo-chief-commons';
import { CapacitySearch } from '@domain/interface/capacity-search';

declare var google: any;

export function GET_CAPACITY_SEARCH_QUERY_PARAMS(search: CapacitySearch) {
	let result: any = {};
	if (search.origin) {
		result['origin_city'] = search.origin.city ? search.origin.city : null;
		result['origin_state'] = search.origin.state ? search.origin.state : null;
		result['origin_postcode'] = search.origin.zip ? search.origin.zip : null;
		result['origin_country'] = search.origin.country ? search.origin.country : null;
		result['origin_latitude'] = search.origin.latitude ? search.origin.latitude : null;
		result['origin_longitude'] = search.origin.longitude ? search.origin.longitude : null;
	}
	if (search.destination) {
		result['destination_city'] = search.destination.city ? search.destination.city : null;
		result['destination_state'] = search.destination.state ? search.destination.state : null;
		result['destination_postcode'] = search.destination.zip ? search.destination.zip : null;
		result['destination_country'] = search.destination.country
			? search.destination.country
			: null;
		result['destination_latitude'] = search.destination.latitude
			? search.destination.latitude
			: null;
		result['destination_longitude'] = search.destination.longitude
			? search.destination.longitude
			: null;
	}
	if (search.pickupDateEnd) {
		result['delivery_date'] = search.pickupDateEnd;
	}
	if (search.pickupDateStart) {
		result['pickup_date'] = search.pickupDateStart;
	}
	if (search.trailerType) {
		result['trailer_type'] = search.trailerType;
	}
	result['hazmat'] = search.hazmat;
	result['has_teams'] = search.hasTeams;
	Object.keys(result).forEach((key) => {
		if (!result[key]) delete result[key];
	});
	return result;
}

export async function BUILD_CAPACITY_SEARCH(params: any) {
	let result: any = {};
	if (IS_NULL_OR_UNDEFINED(params)) {
		return null;
	}
	if (params.origin_city) {
		result['origin'] = {
			city: params.origin_city || null,
			state: params.origin_state || null,
			zip: params.origin_postcode || null,
			country: params.origin_country || null,
			latitude: +params.origin_latitude || null,
			longitude: +params.origin_longitude || null,
		};
		if (
			IS_EMPTY(params.origin_latitude) ||
			IS_EMPTY(params.origin_longitude) ||
			IS_EMPTY(params.origin_postcode)
		) {
			const location = await GET_LAT_LNG_ZIP(
				params.origin_city,
				params.origin_state,
				params.origin_postcode
			);
			SET_LAT_LNG_ZIP(result?.origin, location);
		}
	}
	if (params.destination_city) {
		result['destination'] = {
			city: params.destination_city || null,
			state: params.destination_state || null,
			zip: params.destination_postcode || null,
			country: params.destination_country || null,
			latitude: +params.destination_latitude || null,
			longitude: +params.destination_longitude || null,
		};
		if (
			IS_EMPTY(params.destination_latitude) ||
			IS_EMPTY(params.destination_longitude) ||
			IS_EMPTY(params.destination_postcode)
		) {
			const location = await GET_LAT_LNG_ZIP(
				params.destination_city,
				params.destination_state,
				params.destination_postcode
			);
			SET_LAT_LNG_ZIP(result?.destination, location);
		}
	}
	if (params.delivery_date) {
		result['pickupDateEnd'] = params.delivery_date;
	}
	if (params.pickup_date) {
		result['pickupDateStart'] = params.pickup_date;
	}
	if (params.trailer_type) {
		result['trailerType'] = params.trailer_type;
	}
	result['hazmat'] = params.hazmat;
	result['hasTeams'] = params.has_teams;
	return result;
}

export async function GET_LAT_LNG_ZIP(city: string, state: string, postcode: string) {
	let addressRequest = '',
		result = { lat: null, lng: null, zip: null },
		isZipEmpty = IS_EMPTY(postcode);
	if (IS_NOT_EMPTY(postcode)) {
		addressRequest += `zipcode ${postcode}`;
	}
	if (IS_EMPTY(addressRequest) && IS_NOT_EMPTY(city) && IS_NOT_EMPTY(state)) {
		addressRequest += `city ${city}`;
		addressRequest += `state ${state}`;
	}
	try {
		const geocoder = new google.maps.Geocoder();
		const results = await geocoder.geocode({ address: addressRequest });
		const location = results?.results[0]?.geometry?.location;
		if (IS_NOT_NULL_OR_UNDEFINED(location)) {
			result.lat = location.lat();
			result.lng = location.lng();
			if (isZipEmpty) {
				if (
					IS_EMPTY(result.zip) &&
					IS_NOT_NULL_OR_UNDEFINED(result.lat) &&
					IS_NOT_NULL_OR_UNDEFINED(result.lng)
				) {
					const latLng = new google.maps.LatLng(result.lat, result.lng);
					const places = await geocoder.geocode({ location: latLng });
					const placesResultsList = places?.results || [];
					for (let placesIndex = 0; placesIndex < placesResultsList.length; placesIndex++) {
						for (let i = 0; i < placesResultsList[placesIndex].address_components.length; i++) {
							const postalCodeCheck =
								placesResultsList[placesIndex].address_components[i].types.indexOf('postal_code');
							if (
								postalCodeCheck !== -1 &&
								IS_NOT_NULL_OR_UNDEFINED(
									placesResultsList[placesIndex].address_components[i].long_name
								)
							) {
								result.zip = placesResultsList[placesIndex].address_components[i].long_name;
								break;
							}
						}
						if (IS_NOT_EMPTY(result.zip)) {
							break;
						}
					}
				}
			}
		}
	} catch (error) {
		console.warn('geocode error: ', error);
	}
	return result;
}

export function SET_LAT_LNG_ZIP(address: any, location: any) {
	if (IS_EMPTY(address?.latitude) && IS_NOT_NULL_OR_UNDEFINED(location.lat)) {
		address.latitude = location.lat;
	}
	if (IS_EMPTY(address?.longitude) && IS_NOT_NULL_OR_UNDEFINED(location.lng)) {
		address.longitude = location.lng;
	}
	if (IS_EMPTY(address?.zip) && IS_NOT_NULL_OR_UNDEFINED(location.zip)) {
		address.zip = location.zip;
	}
}

export function BUILD_CAPACITY_SEARCH_FROM_PROJECT(
	projectDTO: ProjectDTO,
	resultCount: number = null
): CapacitySearch {
	let search = {} as CapacitySearch;
	search.origin = {} as Location;
	search.destination = {} as Location;
	search.origin.city = projectDTO.originCity;
	search.origin.state = projectDTO.originState;
	search.origin.zip = projectDTO.originZip;
	search.origin.latitude = projectDTO.originLat;
	search.origin.longitude = projectDTO.originLng;
	search.destination.city = projectDTO.destCity;
	search.destination.state = projectDTO.destState;
	search.destination.zip = projectDTO.destZip;
	search.destination.latitude = projectDTO.destLat;
	search.destination.longitude = projectDTO.destLng;
	search.trailerType = projectDTO.equipmentType;
	search.pickupDateEnd = DATE_YMD(TOMORROW());
	search.pickupDateStart = DATE_YMD(TOMORROW());
	search.resultCount = resultCount;
	search.hasTeams = projectDTO.requireTeams;
	return search;
}
